import React, { useEffect, useState, useRef } from 'react';
import { useOrganization } from '../contexts/OrganizationContext';
import { useToast } from '../contexts/ToastProvider';
import { useAuth } from '../contexts/AuthContext';
import { ShieldCheckIcon, CheckIcon, XIcon, PlusIcon, InfoIcon } from 'lucide-react';
import { ROLES } from '../schemas/mockData';
import type { Role, Permission } from '../schemas/types';
export function RolesAndAccess() {
  const {
    language,
    currentOrganization,
    getHierarchyLabels,
    t
  } = useOrganization();
  const {
    showToast
  } = useToast();
  const {
    isAdmin
  } = useAuth();
  const [roles, setRoles] = useState<Role[]>(ROLES);
  const tableRef = useRef<HTMLDivElement>(null);
  const [hasScroll, setHasScroll] = useState(false);
  const hierarchyLabels = getHierarchyLabels();
  const translations = {
    title: language === 'en' ? 'Roles & Access Control' : language === 'uk' ? 'Ролі та контроль доступу' : "Rôles et contrôle d'accès",
    subtitle: language === 'en' ? 'Manage hierarchical permissions and role-based access' : language === 'uk' ? 'Керуйте ієрархічними дозволами та доступом на основі ролей' : "Gérer les permissions hiérarchiques et l'accès basé sur les rôles",
    addRole: language === 'en' ? 'Add Role' : language === 'uk' ? 'Додати роль' : 'Ajouter un rôle',
    hierarchicalPermissionModel: language === 'en' ? 'Hierarchical Permission Model' : language === 'uk' ? 'Модель ієрархічних дозволів' : 'Modèle de permissions hiérarchiques',
    permissionInfo: language === 'en' ? 'Permissions are scoped to hierarchy levels:' : language === 'uk' ? 'Дозволи обмежені рівнями ієрархії:' : 'Les permissions sont limitées aux niveaux hiérarchiques:',
    role: language === 'en' ? 'Role' : language === 'uk' ? 'Роль' : 'Rôle',
    hierarchyLevel: language === 'en' ? 'Hierarchy Level' : language === 'uk' ? 'Рівень ієрархії' : 'Niveau hiérarchique',
    permissionScopes: language === 'en' ? 'Permission Scopes' : language === 'uk' ? 'Області дозволів' : 'Portées des permissions',
    globalAccess: language === 'en' ? 'AgileBrain-wide access' : language === 'uk' ? 'Доступ на рівні AgileBrain' : 'Accès AgileBrain',
    organizationAccess: language === 'en' ? 'Client organization access' : language === 'uk' ? 'Доступ до організації клієнта' : 'Accès organisation cliente',
    schoolAccess: language === 'en' ? 'School-level access' : language === 'uk' ? 'Доступ на рівні школи' : 'Accès niveau école',
    cohortAccess: language === 'en' ? 'Cohort-level access' : language === 'uk' ? 'Доступ на рівні когорти' : 'Accès niveau cohorte',
    createExercises: language === 'en' ? 'Create Exercises' : language === 'uk' ? 'Створювати вправи' : 'Créer des exercices',
    assignExercises: language === 'en' ? 'Assign Exercises' : language === 'uk' ? 'Призначати вправи' : 'Assigner des exercices',
    viewResults: language === 'en' ? 'View Results' : language === 'uk' ? 'Переглядати результати' : 'Voir les résultats',
    editParticipants: language === 'en' ? 'Edit Participants' : language === 'uk' ? 'Редагувати учасників' : 'Modifier les participants',
    manageHierarchy: language === 'en' ? 'Manage Hierarchy' : language === 'uk' ? 'Керувати ієрархією' : 'Gérer la hiérarchie',
    viewAnalytics: language === 'en' ? 'View Analytics' : language === 'uk' ? 'Переглядати аналітику' : 'Voir les analyses',
    manageIntegrations: language === 'en' ? 'Manage Integrations' : language === 'uk' ? 'Керувати інтеграціями' : 'Gérer les intégrations',
    school: language === 'en' ? hierarchyLabels.subOrganization : language === 'uk' ? 'Школа' : 'École',
    grade: language === 'en' ? hierarchyLabels.group : language === 'uk' ? 'Клас' : 'Classe',
    classroom: language === 'en' ? hierarchyLabels.subGroup : language === 'uk' ? 'Кабінет' : 'Salle de classe'
  };
  // Check for horizontal scroll
  useEffect(() => {
    const checkScroll = () => {
      if (tableRef.current) {
        const hasHorizontalScroll = tableRef.current.scrollWidth > tableRef.current.clientWidth;
        setHasScroll(hasHorizontalScroll);
      }
    };
    checkScroll();
    window.addEventListener('resize', checkScroll);
    return () => window.removeEventListener('resize', checkScroll);
  }, []);
  const allActions: Permission['action'][] = ['create-exercises', 'assign-exercises', 'view-results', 'edit-participants', 'manage-hierarchy', 'view-analytics', 'manage-integrations'];
  const getActionLabel = (action: Permission['action']): string => {
    const labels: Record<Permission['action'], string> = {
      'create-exercises': translations.createExercises,
      'assign-exercises': translations.assignExercises,
      'view-results': translations.viewResults,
      'edit-participants': translations.editParticipants,
      'manage-hierarchy': translations.manageHierarchy,
      'view-analytics': translations.viewAnalytics,
      'manage-integrations': translations.manageIntegrations
    };
    return labels[action];
  };
  const getScopeColor = (scope?: Permission['scope']): string => {
    if (!scope) return 'bg-gray-100 text-gray-600 border-gray-300';
    const colors: Record<NonNullable<Permission['scope']>, string> = {
      global: 'bg-purple-100 text-purple-700 border-purple-300',
      organization: 'bg-blue-100 text-blue-700 border-blue-300',
      school: 'bg-green-100 text-green-700 border-green-300',
      cohort: 'bg-orange-100 text-orange-700 border-orange-300'
    };
    return colors[scope];
  };
  const hasPermission = (role: Role, action: Permission['action']): Permission | undefined => {
    return role.permissions.find(p => p.action === action && p.allowed);
  };
  const togglePermission = (roleId: string, action: Permission['action']) => {
    setRoles(prev => prev.map(role => {
      if (role.id !== roleId) return role;
      const existingPermission = role.permissions.find(p => p.action === action);
      if (existingPermission) {
        return {
          ...role,
          permissions: role.permissions.map(p => p.action === action ? {
            ...p,
            allowed: !p.allowed
          } : p)
        };
      } else {
        return {
          ...role,
          permissions: [...role.permissions, {
            action,
            allowed: true,
            scope: role.hierarchyLevel
          }]
        };
      }
    }));
  };
  return <div className="page-container" data-id="element-2431">
      {/* Page Header */}
      <div className="page-header" data-id="element-2432">
        <div className="page-header-content" data-id="element-2433">
          <div className="flex items-center justify-between" data-id="element-2434">
            <div className="flex items-center gap-3" data-id="element-2435">
              <div className="w-11 h-11 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center shadow-sm" data-id="element-2436">
                <ShieldCheckIcon className="w-6 h-6 text-white" data-id="element-2437" />
              </div>
              <div data-id="element-2438">
                <h1 className="text-2xl font-bold text-gray-900" data-id="element-2439">
                  {translations.title}
                </h1>
                <p className="text-sm text-gray-600 mt-1" data-id="element-2440">
                  {translations.subtitle}
                </p>
              </div>
            </div>
            <button className="px-4 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-all font-medium shadow-sm hover:shadow flex items-center gap-2" data-id="element-2441">
              <PlusIcon className="w-4 h-4" data-id="element-2442" />
              {translations.addRole}
            </button>
          </div>
        </div>
      </div>
      {/* Main Content */}
      <div className="page-main" data-id="element-2443">
        <div className="page-main-content" data-id="element-2444">
          {/* Info Banner */}
          <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4 mb-6 flex items-start gap-3" data-id="element-2445">
            <InfoIcon className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" data-id="element-2446" />
            <div className="text-sm text-blue-900" data-id="element-2447">
              <p className="font-semibold mb-1" data-id="element-2448">
                {translations.hierarchicalPermissionModel}
              </p>
              <p data-id="element-2449">
                {translations.permissionInfo}{' '}
                <span className="font-mono bg-blue-100 px-1.5 py-0.5 rounded" data-id="element-2450">
                  global
                </span>{' '}
                (AgileBrain),{' '}
                <span className="font-mono bg-blue-100 px-1.5 py-0.5 rounded" data-id="element-2451">
                  organization
                </span>{' '}
                (client),{' '}
                <span className="font-mono bg-blue-100 px-1.5 py-0.5 rounded" data-id="element-2452">
                  school
                </span>
                , and{' '}
                <span className="font-mono bg-blue-100 px-1.5 py-0.5 rounded" data-id="element-2453">
                  cohort
                </span>
                .
              </p>
            </div>
          </div>
          {/* Permission Matrix */}
          <div className="content-card" data-id="element-2454">
            <div ref={tableRef} className={`table-scrollable ${hasScroll ? 'has-scroll' : ''}`} data-id="element-2455">
              <table className="permission-matrix" data-id="element-2456">
                <thead data-id="element-2457">
                  <tr data-id="element-2458">
                    <th data-id="element-2459">
                      <div className="font-bold text-gray-900 text-sm mb-1" data-id="element-2460">
                        {translations.role}
                      </div>
                      <div className="text-xs text-gray-600 font-normal" data-id="element-2461">
                        {translations.hierarchyLevel}
                      </div>
                    </th>
                    {allActions.map(action => <th key={action} data-id="element-2462">
                        <div className="font-semibold text-gray-900 text-xs leading-tight" data-id="element-2463">
                          {getActionLabel(action)}
                        </div>
                      </th>)}
                  </tr>
                </thead>
                <tbody data-id="element-2464">
                  {roles.map(role => <tr key={role.id} data-id="element-2465">
                      <td data-id="element-2466">
                        <div className="font-semibold text-gray-900 text-sm mb-2" data-id="element-2467">
                          {role.displayName}
                        </div>
                        <span className={`scope-badge border ${getScopeColor(role.hierarchyLevel)}`} data-id="element-2468">
                          {role.hierarchyLevel || 'undefined'}
                        </span>
                      </td>
                      {allActions.map(action => {
                    const permission = hasPermission(role, action);
                    return <td key={action} data-id="element-2469">
                            <div className="flex flex-col items-center justify-center gap-2" data-id="element-2470">
                              <button onClick={() => togglePermission(role.id, action)} className={`permission-toggle ${permission ? 'allowed' : 'denied'}`} title={permission ? `Allowed (${permission.scope})` : 'Not allowed'} data-id="element-2471">
                                {permission ? <CheckIcon className="w-5 h-5 text-green-700" data-id="element-2472" /> : <XIcon className="w-5 h-5 text-gray-400" data-id="element-2473" />}
                              </button>
                              {permission?.scope && <span className={`scope-badge border ${getScopeColor(permission.scope)}`} data-id="element-2474">
                                  {permission.scope}
                                </span>}
                            </div>
                          </td>;
                  })}
                    </tr>)}
                </tbody>
              </table>
            </div>
          </div>
          {/* Legend */}
          <div className="mt-6 bg-gray-50 rounded-lg p-6 border-2 border-gray-200" data-id="element-2475">
            <h3 className="font-bold text-gray-900 mb-4 text-base" data-id="element-2476">
              {translations.permissionScopes}
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-2477">
              <div className="flex items-center gap-2" data-id="element-2478">
                <span className="scope-badge border bg-purple-100 text-purple-700 border-purple-300" data-id="element-2479">
                  global
                </span>
                <span className="text-sm text-gray-600" data-id="element-2480">
                  {translations.globalAccess}
                </span>
              </div>
              <div className="flex items-center gap-2" data-id="element-2481">
                <span className="scope-badge border bg-blue-100 text-blue-700 border-blue-300" data-id="element-2482">
                  organization
                </span>
                <span className="text-sm text-gray-600" data-id="element-2483">
                  {translations.organizationAccess}
                </span>
              </div>
              <div className="flex items-center gap-2" data-id="element-2484">
                <span className="scope-badge border bg-green-100 text-green-700 border-green-300" data-id="element-2485">
                  school
                </span>
                <span className="text-sm text-gray-600" data-id="element-2486">
                  {translations.schoolAccess}
                </span>
              </div>
              <div className="flex items-center gap-2" data-id="element-2487">
                <span className="scope-badge border bg-orange-100 text-orange-700 border-orange-300" data-id="element-2488">
                  cohort
                </span>
                <span className="text-sm text-gray-600" data-id="element-2489">
                  {translations.cohortAccess}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}